import React, { createContext, useCallback, useContext, useMemo, useState, useEffect, useRef } from 'react';
import type { KianEntityContext, KianPageContextValue } from '../types/kian';
import { entityKey } from '../types/kian';

const PINNED_KEY = 'kian.v2.pinned';
const RECENT_KEY = 'kian.v2.recent';

const PageContext = createContext<KianPageContextValue | null>(null);

function loadPinned(): KianEntityContext[] {
  try {
    const raw = localStorage.getItem(PINNED_KEY);
    if (!raw) return [];
    const j = JSON.parse(raw) as KianEntityContext[];
    return Array.isArray(j) ? j : [];
  } catch {
    return [];
  }
}

function savePinned(p: KianEntityContext[]) {
  try {
    localStorage.setItem(PINNED_KEY, JSON.stringify(p));
  } catch {
    /* ignore */
  }
}

function loadRecent(): KianEntityContext[] {
  try {
    const raw = sessionStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const j = JSON.parse(raw) as KianEntityContext[];
    return Array.isArray(j) ? j.slice(0, 10) : [];
  } catch {
    return [];
  }
}

function saveRecent(r: KianEntityContext[]) {
  try {
    sessionStorage.setItem(RECENT_KEY, JSON.stringify(r.slice(0, 10)));
  } catch {
    /* ignore */
  }
}

export function PageContextProvider({ children }: { children: React.ReactNode }) {
  const [current, setCurrent] = useState<KianEntityContext | null>(null);
  const [recent, setRecent] = useState<KianEntityContext[]>(() => loadRecent());
  const [pinned, setPinned] = useState<KianEntityContext[]>(() => loadPinned());

  useEffect(() => {
    savePinned(pinned);
  }, [pinned]);

  useEffect(() => {
    saveRecent(recent);
  }, [recent]);

  const register = useCallback((ctx: Omit<KianEntityContext, 'visitedAt'>) => {
    const full: KianEntityContext = {
      ...ctx,
      visitedAt: Date.now(),
    };
    setCurrent((prev) => {
      if (prev && entityKey(prev) === entityKey(full)) {
        return { ...full, visitedAt: Date.now() };
      }
      return full;
    });
  }, []);

  // Beim Wechsel von `current` den alten Eintrag in `recent` verschieben (entkoppelt vom setCurrent-Callback).
  const previousCurrentRef = useRef<KianEntityContext | null>(null);
  useEffect(() => {
    const prev = previousCurrentRef.current;
    if (prev && (!current || entityKey(prev) !== entityKey(current))) {
      setRecent((r) => {
        const next = [prev, ...r.filter((x) => entityKey(x) !== entityKey(prev))];
        return next.slice(0, 10);
      });
    }
    previousCurrentRef.current = current;
  }, [current]);

  const unregister = useCallback(() => {
    setCurrent(null);
  }, []);

  const pinEntity = useCallback((ctx: KianEntityContext) => {
    const k = entityKey(ctx);
    setPinned((p) => {
      if (p.some((x) => entityKey(x) === k)) return p;
      return [...p, { ...ctx, visitedAt: Date.now() }];
    });
  }, []);

  const unpinEntity = useCallback((key: string) => {
    setPinned((p) => p.filter((x) => entityKey(x) !== key));
  }, []);

  const clearPins = useCallback(() => setPinned([]), []);

  const removeRecent = useCallback((key: string) => {
    setRecent((r) => r.filter((x) => entityKey(x) !== key));
  }, []);

  const value = useMemo<KianPageContextValue>(
    () => ({
      current,
      recent,
      pinned,
      register,
      unregister,
      pinEntity,
      unpinEntity,
      clearPins,
      removeRecent,
    }),
    [current, recent, pinned, register, unregister, pinEntity, unpinEntity, clearPins, removeRecent]
  );

  return <PageContext.Provider value={value}>{children}</PageContext.Provider>;
}

export function useKianPageContext(): KianPageContextValue {
  const v = useContext(PageContext);
  if (!v) throw new Error('useKianPageContext outside PageContextProvider');
  return v;
}
